import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";

const sheetVariants = cva(
  "data-[state=open]:animate-in data-[state=closed]:animate-out absolute z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      variant: {
        default: "blue-sheet bg-[#1D2B53]",
        koth: "koth-sheet bg-[#422136]",
        shadcn: "bg-black! border-white/20!",
      },
    },
  },
);

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  container,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  const resolvedContainer =
    container ??
    (typeof document === "undefined"
      ? undefined
      : (document.getElementById("page-content") ??
        document.body ??
        undefined));

  return (
    <SheetPrimitive.Portal
      data-slot="sheet-portal"
      container={resolvedContainer}
      {...props}
    />
  );
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  variant = "default",
  container,
  shouldShowCloseButton = false,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  variant?: "default" | "koth" | "shadcn";
  container?: HTMLElement | null;
  shouldShowCloseButton?: boolean;
}) {
  return (
    <SheetPortal container={container ?? undefined}>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(
          sheetVariants({ variant }),
          side === "right" &&
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
          side === "left" &&
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" &&
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
          side === "bottom" &&
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
          className,
        )}
        {...props}
      >
        {children}
        {shouldShowCloseButton && (
          <SheetPrimitive.Close className="ring-0! data-[state=open]:bg-secondary absolute top-6 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none">
            <XIcon className="size-4 text-white" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

const SheetCloseIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      className={cn(
        "w-7 h-7 cursor-pointer hover:saturate-98 hover:brightness-98 active:sacle-95",
        className,
      )}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 42 42"
      fill="none"
    >
      <path
        d="M38.9479 31.2938C37.3274 34.5687 34.8738 36.2076 31.5871 36.2105C24.4077 36.2133 17.2269 36.2133 10.0447 36.2105C6.86088 36.2076 4.55588 34.5773 3.12972 31.3194C3.0154 30.7546 2.95823 30.03 2.95823 29.1457C2.96395 22.4759 2.95681 16.6892 2.9368 11.7853C2.92823 9.14936 3.11686 7.41775 3.5027 6.59045C4.30866 4.8731 5.58192 3.63216 7.32246 2.86762C8.1027 2.52529 9.87323 2.35698 12.6341 2.36269C20.8109 2.38266 25.2337 2.40548 25.9025 2.43116C33.349 2.70502 38.8579 8.96108 38.9822 16.3083C39.0422 19.9284 39.0308 24.9236 38.9479 31.2938ZM13.4272 20.3435C13.4272 20.4148 13.4243 20.4534 13.4186 20.4591C12.5669 21.2379 11.7767 21.9482 11.0479 22.5901C10.8191 22.792 10.6407 23.0442 10.5268 23.3265C10.413 23.6088 10.3668 23.9134 10.392 24.2161C10.5249 25.8037 10.0661 26.5226 11.3737 27.8191C12.5769 29.0116 13.4672 29.913 14.0445 30.5235C15.262 31.8073 16.6039 32.2352 17.9629 30.8487C18.2287 30.5749 19.1604 29.5122 20.758 27.6608C20.7609 27.6579 20.7838 27.6508 20.8266 27.6394C20.8466 27.6309 20.8666 27.6266 20.8866 27.6266C20.9066 27.6266 20.9281 27.6309 20.9509 27.6394C20.9938 27.6508 21.0167 27.6579 21.0195 27.6608C22.6172 29.5122 23.5489 30.5749 23.8147 30.8487C25.1737 32.2352 26.5155 31.8073 27.733 30.5192C28.3104 29.9088 29.2006 29.0073 30.4038 27.8149C31.7114 26.5183 31.2527 25.7994 31.3813 24.2118C31.4065 23.9091 31.3603 23.6045 31.2464 23.3222C31.1326 23.0399 30.9541 22.7877 30.7254 22.5858C29.9966 21.9439 29.2064 21.2336 28.3547 20.4548C28.3489 20.4519 28.3461 20.4134 28.3461 20.3392C28.3461 20.2679 28.3489 20.2294 28.3547 20.2237C29.2064 19.4449 29.9966 18.7346 30.7254 18.0927C30.9541 17.8907 31.1326 17.6386 31.2464 17.3563C31.3603 17.074 31.4065 16.7694 31.3813 16.4666C31.2484 14.8791 31.7071 14.1602 30.3996 12.8636C29.1963 11.6712 28.3061 10.7697 27.7287 10.1592C26.5112 8.8755 25.1694 8.44758 23.8104 9.83402C23.5446 10.1079 22.6129 11.1705 21.0153 13.022C21.0124 13.0248 20.9895 13.0319 20.9466 13.0434C20.9266 13.0519 20.9066 13.0562 20.8866 13.0562C20.8666 13.0562 20.8452 13.0519 20.8223 13.0434C20.7795 13.0319 20.7566 13.0248 20.7537 13.022C19.1561 11.1705 18.2244 10.1079 17.9586 9.83402C16.5996 8.44758 15.2578 8.8755 14.0402 10.1635C13.4629 10.774 12.5727 11.6755 11.3694 12.8679C10.0662 14.1645 10.5249 14.8834 10.392 16.4709C10.3668 16.7737 10.413 17.0783 10.5268 17.3606C10.6407 17.6429 10.8191 17.895 11.0479 18.097C11.7767 18.7388 12.5669 19.4492 13.4186 20.228C13.4243 20.2308 13.4272 20.2693 13.4272 20.3435Z"
        fill="#ED243E"
      />
      <path
        d="M20.8861 13.0563C20.9061 13.0563 20.9261 13.052 20.9461 13.0435C20.989 13.032 21.0119 13.0249 21.0147 13.0221C22.6124 11.1706 23.5441 10.108 23.8099 9.83412C25.1689 8.44769 26.5107 8.87561 27.7282 10.1593C28.3055 10.7698 29.1958 11.6713 30.399 12.8637C31.7066 14.1603 31.2479 14.8792 31.3808 16.4668C31.406 16.7695 31.3598 17.0741 31.2459 17.3564C31.132 17.6387 30.9536 17.8909 30.7249 18.0928C29.9961 18.7347 29.2058 19.445 28.3541 20.2238C28.3484 20.2295 28.3456 20.268 28.3456 20.3394C28.3456 20.4135 28.3484 20.452 28.3541 20.4549C29.2058 21.2337 29.9961 21.944 30.7249 22.5859C30.9536 22.7879 31.132 23.04 31.2459 23.3223C31.3598 23.6046 31.406 23.9092 31.3808 24.2119C31.2522 25.7995 31.7109 26.5184 30.4033 27.815C29.2001 29.0074 28.3098 29.9089 27.7325 30.5194C26.515 31.8074 25.1732 32.2353 23.8142 30.8489C23.5484 30.575 22.6166 29.5123 21.019 27.6609C21.0161 27.6581 20.9933 27.6509 20.9504 27.6395C20.9276 27.631 20.9061 27.6267 20.8861 27.6267C20.8661 27.6267 20.8461 27.631 20.8261 27.6395C20.7832 27.6509 20.7604 27.6581 20.7575 27.6609C19.1599 29.5123 18.2281 30.575 17.9623 30.8489C16.6034 32.2353 15.2615 31.8074 14.044 30.5236C13.4667 29.9132 12.5764 29.0117 11.3732 27.8192C10.0656 26.5227 10.5243 25.8038 10.3915 24.2162C10.3663 23.9135 10.4124 23.6089 10.5263 23.3266C10.6402 23.0443 10.8186 22.7921 11.0474 22.5902C11.7762 21.9483 12.5664 21.238 13.4181 20.4592C13.4238 20.4535 13.4267 20.4149 13.4267 20.3436C13.4267 20.2695 13.4238 20.2309 13.4181 20.2281C12.5664 19.4493 11.7762 18.739 11.0474 18.0971C10.8186 17.8951 10.6402 17.643 10.5263 17.3607C10.4124 17.0784 10.3663 16.7738 10.3915 16.471C10.5243 14.8835 10.0656 14.1646 11.3689 12.868C12.5721 11.6756 13.4624 10.7741 14.0397 10.1636C15.2572 8.87561 16.5991 8.44769 17.9581 9.83412C18.2239 10.108 19.1556 11.1706 20.7532 13.0221C20.7561 13.0249 20.7789 13.032 20.8218 13.0435C20.8447 13.052 20.8661 13.0563 20.8861 13.0563ZM13.7353 12.3545C12.5178 13.5698 11.8576 14.2345 11.7547 14.3486C11.6325 14.487 11.5699 14.6679 11.5803 14.8521C11.5908 15.0363 11.6734 15.209 11.8105 15.3328C13.8897 17.2028 16.3504 19.3423 16.3504 19.4921C16.3504 19.6376 13.8897 21.7771 11.8105 23.6471C11.6734 23.7709 11.5908 23.9436 11.5803 24.1278C11.5699 24.312 11.6325 24.4929 11.7547 24.6313C11.8576 24.7454 12.5178 25.4101 13.7353 26.6254C14.9529 27.8406 15.6174 28.4982 15.7288 28.598C15.867 28.7205 16.0479 28.7838 16.2324 28.7742C16.4169 28.7646 16.5902 28.6829 16.7148 28.5467C18.5926 26.4713 20.7318 24.0151 20.8818 24.0151C21.0319 24.0151 23.1754 26.4713 25.0488 28.5467C25.1735 28.6829 25.3468 28.7646 25.5313 28.7742C25.7158 28.7838 25.8967 28.7205 26.0348 28.598C26.1492 28.4982 26.8151 27.8406 28.0326 26.6254C29.2501 25.4101 29.9089 24.7454 30.0089 24.6313C30.1311 24.4929 30.1938 24.312 30.1833 24.1278C30.1729 23.9436 30.0902 23.7709 29.9532 23.6471C27.874 21.7771 25.4175 19.6376 25.4175 19.4921C25.4175 19.3423 27.874 17.2028 29.9532 15.3328C30.0902 15.209 30.1729 15.0363 30.1833 14.8521C30.1938 14.6679 30.1311 14.487 30.0089 14.3486C29.9089 14.2345 29.2501 13.5698 28.0326 12.3545C26.8151 11.1393 26.1492 10.4817 26.0348 10.3819C25.8967 10.2594 25.7158 10.1961 25.5313 10.2057C25.3468 10.2153 25.1735 10.297 25.0488 10.4332C23.1711 12.5086 21.0319 14.9648 20.8818 14.9648C20.7318 14.9648 18.5926 12.5086 16.7148 10.4332C16.5902 10.297 16.4169 10.2153 16.2324 10.2057C16.0479 10.1961 15.867 10.2594 15.7288 10.3819C15.6174 10.4817 14.9529 11.1393 13.7353 12.3545Z"
        fill="#8B0235"
      />
      <path
        d="M20.8817 14.9646C21.0318 14.9646 23.171 12.5084 25.0487 10.433C25.1733 10.2969 25.3467 10.2152 25.5312 10.2055C25.7157 10.1959 25.8966 10.2592 26.0347 10.3817C26.1491 10.4815 26.815 11.1391 28.0325 12.3544C29.25 13.5696 29.9088 14.2343 30.0088 14.3484C30.131 14.4869 30.1937 14.6678 30.1832 14.8519C30.1728 15.0361 30.0901 15.2088 29.9531 15.3326C27.8739 17.2026 25.4174 19.3422 25.4174 19.4919C25.4174 19.6374 27.8739 21.777 29.9531 23.6469C30.0901 23.7707 30.1728 23.9434 30.1832 24.1276C30.1937 24.3118 30.131 24.4927 30.0088 24.6311C29.9088 24.7453 29.25 25.4099 28.0325 26.6252C26.815 27.8405 26.1491 28.498 26.0347 28.5979C25.8966 28.7204 25.7157 28.7837 25.5312 28.774C25.3467 28.7644 25.1733 28.6827 25.0487 28.5465C23.1753 26.4712 21.0318 24.015 20.8817 24.015C20.7317 24.015 18.5924 26.4712 16.7147 28.5465C16.5901 28.6827 16.4168 28.7644 16.2323 28.774C16.0478 28.7837 15.8669 28.7204 15.7287 28.5979C15.6172 28.498 14.9527 27.8405 13.7352 26.6252C12.5177 25.4099 11.8575 24.7453 11.7546 24.6311C11.6324 24.4927 11.5698 24.3118 11.5802 24.1276C11.5907 23.9434 11.6733 23.7707 11.8104 23.6469C13.8896 21.777 16.3503 19.6374 16.3503 19.4919C16.3503 19.3422 13.8896 17.2026 11.8104 15.3326C11.6733 15.2088 11.5907 15.0361 11.5802 14.8519C11.5698 14.6678 11.6324 14.4869 11.7546 14.3484C11.8575 14.2343 12.5177 13.5696 13.7352 12.3544C14.9527 11.1391 15.6172 10.4815 15.7287 10.3817C15.8669 10.2592 16.0478 10.1959 16.2323 10.2055C16.4168 10.2152 16.5901 10.2969 16.7147 10.433C18.5924 12.5084 20.7317 14.9646 20.8817 14.9646Z"
        fill="#FEFDEB"
      />
      <path
        d="M38.9481 31.2937C38.7652 34.5344 37.4348 36.6711 34.9569 37.7038C33.7537 38.203 31.6945 38.4384 28.7793 38.4099C21.9172 38.3385 16.3497 38.3343 12.077 38.397C9.75628 38.4313 8.07433 38.2088 7.03115 37.7295C4.88763 36.7453 2.50405 33.951 3.12995 31.3194C4.55611 34.5772 6.86111 36.2076 10.0449 36.2104C17.2272 36.2133 24.4079 36.2133 31.5873 36.2104C34.874 36.2076 37.3276 34.5686 38.9481 31.2937Z"
        fill="#8B0235"
      />
      <path
        d="M29.7178 0.950527C35.4152 2.64506 39.3293 7.01404 40.7526 12.8507C41.0241 13.9576 41.1513 15.4867 41.1342 17.438C41.0884 23.3117 41.087 27.3798 41.1299 29.642C41.1813 32.3806 40.987 34.3133 40.5468 35.4402C39.5322 38.0419 37.7417 39.8191 35.1752 40.772C34.152 41.1514 32.6158 41.3396 30.5666 41.3368C18.763 41.314 12.6282 41.3111 12.1623 41.3282C9.73874 41.4081 7.92389 41.2141 6.71781 40.7463C4.13987 39.7421 2.36932 37.972 1.40616 35.4359C1.01175 34.3946 0.818848 32.9255 0.827422 31.0284C0.853144 24.2075 0.853144 17.4009 0.827422 10.6085C0.818848 8.67147 1.01462 7.1852 1.41474 6.14965C2.3979 3.61642 4.13701 1.86055 6.63206 0.88206C7.70382 0.462707 9.43151 0.263018 11.8151 0.282987C16.4594 0.31722 20.1477 0.315789 22.8799 0.278703C25.7951 0.238765 28.0744 0.462708 29.7178 0.950527ZM3.12956 31.3194C2.50365 33.951 4.88723 36.7453 7.03075 37.7295C8.07393 38.2088 9.75589 38.4313 12.0766 38.397C16.3494 38.3343 21.9168 38.3386 28.7789 38.4099C31.6941 38.4384 33.7533 38.2031 34.9565 37.7038C37.4344 36.6711 38.7648 34.5344 38.9478 31.2937C39.0306 24.9235 39.0421 19.9284 38.9821 16.3083C38.8577 8.96103 33.3489 2.70497 25.9023 2.4311C25.2335 2.40543 20.8107 2.3826 12.6339 2.36264C9.87306 2.35693 8.10253 2.52524 7.32229 2.86757C5.58176 3.6321 4.30849 4.87305 3.50253 6.5904C3.1167 7.4177 2.92806 9.1493 2.93664 11.7852C2.95664 16.6891 2.96378 22.4759 2.95807 29.1456C2.95807 30.03 3.01524 30.7545 3.12956 31.3194Z"
        fill="black"
      />
    </svg>
  );
};

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetOverlay,
  SheetCloseIcon,
};
